import { useCallback } from "react";
import { Atom, atom } from "jotai";
import {
  atomFamily,
  atomWithStorage,
  useAtomValue,
  useUpdateAtom,
} from "jotai/utils";
import fastDeepEqual from "fast-deep-equal";

import { TransformData } from "interfaces";
import { fileColTypes } from "modules/data-files/api";

const PREFIX = `data-files/transform/stack`;

const columnsToDrop = atomFamily((fileId: string) =>
  atomWithStorage<string[]>(`${PREFIX}/${fileId}/columns-to-drop`, [])
);

export const useGetColDropStatusInFile = (fileId: string, column: string) => {
  return (useAtomValue(columnsToDrop(fileId)) || []).includes(column);
};

const colDropStatusAtom = atomFamily(
  ({ fileIds, column }: { fileIds: string[]; column: string }) =>
    atom(
      (get) =>
        Object.fromEntries(
          fileIds.map((f) => [
            f,
            get(columnsToDrop(f)).includes(column) ? "drop" : "keep",
          ])
        ) as { [fileId: string]: "keep" | "drop" },
      (get, set, update: "keep" | "drop") => {
        const nameReversers = get(fileColumnRenamerReversers(fileIds));
        fileIds.forEach((f) => {
          const currDroppedColumns = get(columnsToDrop(f)) || [];
          const origName = nameReversers[f]?.[column] || column;

          if (update === "keep" && currDroppedColumns.includes(origName)) {
            set(
              columnsToDrop(f),
              currDroppedColumns.filter((c) => c !== origName)
            );
          } else if (
            update === "drop" &&
            !currDroppedColumns.includes(origName)
          ) {
            set(columnsToDrop(f), [...currDroppedColumns, origName]);
          }
        });
      }
    ),
  fastDeepEqual
);

export const useGetColDropStatus = (fileIds: string[], column: string) =>
  useAtomValue(colDropStatusAtom({ fileIds, column }));

export const useDropColumn = (fileIds: string[], column: string) => {
  const updater = useUpdateAtom(colDropStatusAtom({ fileIds, column }));
  return useCallback(() => updater("drop"), [updater, fileIds, column]);
};

export const useKeepColumn = (fileIds: string[], column: string) => {
  const updater = useUpdateAtom(colDropStatusAtom({ fileIds, column }));
  return useCallback(() => updater("keep"), [updater, fileIds, column]);
};

const colRenamer = atomFamily((fileId: string) =>
  atomWithStorage<{ [colName: string]: string }>(
    `${PREFIX}/${fileId}/renamer`,
    {}
  )
);

export const useGetRenamerForFile = (fileId: string) =>
  useAtomValue(colRenamer(fileId));

export const useGetOriginalName = (fileId: string, column: string) => {
  const renamer = useAtomValue(colRenamer(fileId));
  const reverser = Object.fromEntries(
    Object.entries(renamer).map(([k, v]) => [v, k])
  );

  if (!!reverser[column]) {
    return reverser[column];
  } else if (!!renamer[column]) {
    return null;
  } else {
    return column;
  }
};

const fileColumnRenamers = atomFamily(
  (fileIds: string[]) =>
    atom((get) =>
      Object.fromEntries(fileIds.map((f) => [f, get(colRenamer(f))]))
    ),
  fastDeepEqual
);

const fileColumnRenamerReversers = atomFamily(
  (fileIds: string[]) =>
    atom((get) =>
      Object.fromEntries(
        fileIds.map((f) => [
          f,
          Object.fromEntries(
            Object.entries(get(colRenamer(f))).map(([k, v]) => [v, k])
          ),
        ])
      )
    ),
  fastDeepEqual
);

export const useGetFileColumnRenamers = (fileIds: string[]) =>
  useAtomValue(fileColumnRenamers(fileIds));

export const useGetFileColumnRenameReversers = (fileIds: string[]) =>
  useAtomValue(fileColumnRenamerReversers(fileIds));

const newColumnName = atomFamily(
  ({ fileIds, column }: { fileIds: string[]; column: string }) =>
    atom(
      (get) =>
        Object.fromEntries(
          fileIds.map((f) => [f, get(colRenamer(f))[column] || column])
        ),
      (get, set, update: string | null) => {
        fileIds.forEach((f) => {
          const renamer = get(colRenamer(f));
          const reverser = Object.fromEntries(
            Object.entries(renamer).map(([k, v]) => [v, k])
          );
          const origName = reverser[column] || column;
          const curr = { ...renamer };
          if (update === null || update === undefined || update === f) {
            delete curr[origName];
          } else {
            curr[origName] = update;
          }
          set(colRenamer(f), curr);
        });
      }
    ),
  fastDeepEqual
);

export const useUpdateColumnName = (fileIds: string[], column: string) =>
  useUpdateAtom(newColumnName({ fileIds, column }));

export const useGetNewColumnName = (fileIds: string[], column: string) =>
  useAtomValue(newColumnName({ fileIds, column }));

const specGetterAtom = atomFamily(
  (fileIds: string[]) =>
    atom<Omit<TransformData.StackSpec, "name">>((get) => {
      const colsToDrop = Object.fromEntries(
        fileIds.map((f) => [f, get(columnsToDrop(f)) || []])
      );

      const renamers = Object.fromEntries(
        fileIds
          .map((f) => [f, get(colRenamer(f))])
          .filter((r) => Object.keys(r[1] || {}).length >= 1)
      );

      const colTypes = Object.fromEntries(
        fileIds.map((f) => [f, get(fileColTypes(f))])
      );

      return {
        source_data: fileIds,
        column_types: colTypes,
        columns_to_drop: colsToDrop,
        column_renamers: renamers,
      };
    }),
  fastDeepEqual
);

export const useGetSpec = (fileIds: string[]) =>
  useAtomValue(specGetterAtom(fileIds));
